import { PhoneInput } from "@teamworksdev/shadcn";
import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  FormControlLabel,
  Radio,
  RadioGroup,
  TextField,
  Typography,
} from "@mui/material";
import { useState } from "react";
import DialogTitle from "../../../../componentLibrary/DialogTitle";
import { useMenuOrderGuestForPhoneNumberKioskLazyQuery } from "../../../../types";
import { GuestModalStepper } from "./GuestModalStepper";
import { GuestIdentity } from "./types";

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const isPhoneNumberValid = (phoneNumber: string) => phoneNumber.replace(/[^0-9]/g, "").length >= 7;

interface GuestModalProps {
  open: boolean;
  onClose: () => void;
  onGuestResolved: (guest: GuestIdentity) => void;
}

type SubStep = "guestType" | "newGuest" | "returningGuest";

export const GuestModal = ({ open, onClose, onGuestResolved }: GuestModalProps) => {
  const [subStep, setSubStep] = useState<SubStep>("guestType");
  const [guestTypeChoice, setGuestTypeChoice] = useState<"new" | "returning" | null>(null);

  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [email, setEmail] = useState("");
  const [smsOptIn, setSmsOptIn] = useState(false);

  const [returningPhoneNumber, setReturningPhoneNumber] = useState("");
  const [returningNotFound, setReturningNotFound] = useState(false);

  const [collision, setCollision] = useState<{ existingGuest: GuestIdentity; freshGuest: GuestIdentity } | null>(null);

  const [lookupForNewGuest, { loading: newGuestLookupLoading }] = useMenuOrderGuestForPhoneNumberKioskLazyQuery({
    fetchPolicy: "network-only",
    onCompleted: data => {
      const existing = data.menuOrderGuestForPhoneNumber;
      const freshGuest: GuestIdentity = {
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        phoneNumber,
        email: email.trim() === "" ? null : email.trim(),
        smsOptIn,
      };
      if (existing) {
        setCollision({
          existingGuest: {
            firstName: existing.firstName,
            lastName: existing.lastName,
            phoneNumber: existing.phoneNumber,
            email: existing.email ?? null,
            smsOptIn: existing.smsOptIn,
          },
          freshGuest,
        });
      } else {
        onGuestResolved(freshGuest);
        resetFields();
      }
    },
  });

  const [lookupForReturningGuest, { loading: returningGuestLookupLoading }] = useMenuOrderGuestForPhoneNumberKioskLazyQuery({
    fetchPolicy: "network-only",
    onCompleted: data => {
      const existing = data.menuOrderGuestForPhoneNumber;
      if (existing) {
        onGuestResolved({
          firstName: existing.firstName,
          lastName: existing.lastName,
          phoneNumber: existing.phoneNumber,
          email: existing.email ?? null,
          smsOptIn: existing.smsOptIn,
        });
        resetFields();
      } else {
        setReturningNotFound(true);
      }
    },
  });

  const lookupLoading = newGuestLookupLoading || returningGuestLookupLoading;

  const resetFields = () => {
    setSubStep("guestType");
    setGuestTypeChoice(null);
    setFirstName("");
    setLastName("");
    setPhoneNumber("");
    setEmail("");
    setSmsOptIn(false);
    setReturningPhoneNumber("");
    setReturningNotFound(false);
    setCollision(null);
  };

  const resetAndClose = () => {
    resetFields();
    onClose();
  };

  const emailIsValid = email === "" || EMAIL_REGEX.test(email);
  const newGuestValid = firstName.trim() !== "" && lastName.trim() !== "" && isPhoneNumberValid(phoneNumber) && emailIsValid;

  const handleContinueFromGuestType = () => {
    if (!guestTypeChoice) {
      return;
    }
    setSubStep(guestTypeChoice === "new" ? "newGuest" : "returningGuest");
  };

  const handleStartOrdering = () => {
    lookupForNewGuest({ variables: { phoneNumber } });
  };

  const handleContinueReturning = () => {
    setReturningNotFound(false);
    lookupForReturningGuest({ variables: { phoneNumber: returningPhoneNumber } });
  };

  const handleOrderAsNewGuestFromReturning = () => {
    setPhoneNumber("");
    setReturningNotFound(false);
    setSubStep("newGuest");
  };

  const handleContinueAsNewGuest = () => {
    if (!collision) {
      return;
    }
    onGuestResolved(collision.freshGuest);
    resetFields();
  };

  const handleUseSavedInfo = () => {
    if (!collision) {
      return;
    }
    onGuestResolved(collision.existingGuest);
    resetFields();
  };

  return (
    <>
      <Dialog open={open && !collision} onClose={resetAndClose} maxWidth="sm" fullWidth>
        <DialogTitle title="Order as a Guest" onClose={resetAndClose} />
        <DialogContent>
          <GuestModalStepper activeStep={subStep === "guestType" ? 1 : 2} />
          {subStep === "guestType" && (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2, py: 1 }}>
              <RadioGroup value={guestTypeChoice ?? ""} onChange={e => setGuestTypeChoice(e.target.value as "new" | "returning")}>
                <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                  <Box sx={{ border: 1, borderColor: "grey.300", borderRadius: 1.5, p: 1 }}>
                    <FormControlLabel
                      value="new"
                      control={<Radio size="small" />}
                      sx={{ m: 0, width: "100%", alignItems: "flex-start" }}
                      label={
                        <Box sx={{ pt: 0.5 }}>
                          <Typography variant="body1Semibold">New Guest</Typography>
                          <Typography variant="body2" color="textSecondary">
                            Fill in your details to place an order
                          </Typography>
                        </Box>
                      }
                    />
                  </Box>
                  <Box sx={{ border: 1, borderColor: "grey.300", borderRadius: 1.5, p: 1 }}>
                    <FormControlLabel
                      value="returning"
                      control={<Radio size="small" />}
                      sx={{ m: 0, width: "100%", alignItems: "flex-start" }}
                      label={
                        <Box sx={{ pt: 0.5 }}>
                          <Typography variant="body1Semibold">Returning Guest</Typography>
                          <Typography variant="body2" color="textSecondary">
                            Just enter your phone number to skip ahead
                          </Typography>
                        </Box>
                      }
                    />
                  </Box>
                </Box>
              </RadioGroup>
            </Box>
          )}

          {subStep === "newGuest" && (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2, py: 1 }}>
              <TextField label="First Name" value={firstName} onChange={e => setFirstName(e.target.value)} autoFocus fullWidth />
              <TextField label="Last Name" value={lastName} onChange={e => setLastName(e.target.value)} fullWidth />
              <Box>
                <Typography
                  id="new-guest-phone-label"
                  variant="caption"
                  sx={{ fontWeight: 600, color: "text.secondary", display: "block", mb: 0.5 }}
                >
                  Phone Number
                </Typography>
                <PhoneInput
                  aria-labelledby="new-guest-phone-label"
                  value={phoneNumber}
                  onChange={({ phone }) => setPhoneNumber(phone)}
                  placeholder="(123) 456-7890"
                />
              </Box>
              <TextField
                label="Email (optional)"
                value={email}
                onChange={e => setEmail(e.target.value)}
                error={!emailIsValid}
                helperText={!emailIsValid ? "Enter a valid email" : undefined}
                fullWidth
              />
              <FormControlLabel
                control={<Checkbox checked={smsOptIn} onChange={e => setSmsOptIn(e.target.checked)} />}
                label="Text me when my order is ready"
              />
            </Box>
          )}

          {subStep === "returningGuest" && (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2, py: 1 }}>
              <Box>
                <Typography
                  id="returning-guest-phone-label"
                  variant="caption"
                  sx={{ fontWeight: 600, color: "text.secondary", display: "block", mb: 0.5 }}
                >
                  Phone Number
                </Typography>
                <PhoneInput
                  aria-labelledby="returning-guest-phone-label"
                  value={returningPhoneNumber}
                  onChange={({ phone }) => {
                    setReturningPhoneNumber(phone);
                    setReturningNotFound(false);
                  }}
                  placeholder="(123) 456-7890"
                  isInvalid={returningNotFound}
                />
              </Box>
              {returningNotFound && (
                <Typography variant="body2" color="error">
                  No account found. Try a different phone number or order as a new guest.
                </Typography>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          {subStep === "guestType" && (
            <Button variant="contained" disabled={!guestTypeChoice} onClick={handleContinueFromGuestType}>
              Continue
            </Button>
          )}
          {subStep === "newGuest" && (
            <Button variant="contained" disabled={!newGuestValid || lookupLoading} onClick={handleStartOrdering}>
              Start ordering
            </Button>
          )}
          {subStep === "returningGuest" && (
            <>
              {returningNotFound && (
                <Button variant="outlined" onClick={handleOrderAsNewGuestFromReturning}>
                  Order as New Guest
                </Button>
              )}
              <Button
                variant="contained"
                disabled={!isPhoneNumberValid(returningPhoneNumber) || lookupLoading}
                onClick={handleContinueReturning}
              >
                Continue
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>

      <Dialog open={!!collision} onClose={() => setCollision(null)} maxWidth="sm" fullWidth>
        <DialogTitle title="Existing Account Found" onClose={() => setCollision(null)} />
        <DialogContent>
          <Typography>
            The phone number you entered matches an existing account. Would you like to use your saved info or continue as a new guest?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" onClick={handleContinueAsNewGuest}>
            Continue as New Guest
          </Button>
          <Button variant="contained" onClick={handleUseSavedInfo}>
            Use Saved Info
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
